import {ChangeDetectorRef, Component, Input, OnInit} from '@angular/core';
import {AbstractComponent} from "@components";
import {Router} from '@angular/router';
import {
  ApplicationFormSettings,
  Competitor,
  CompetitorFilter,
  CompetitorStatus,
  Country,
  EventItem,
  FetchEventByIdRequest,
  Lookups,
  PagedFilter,
  Pages,
  Race,
  RaceRegistration,
  RaceSettings,
  RaceStartDateType,
  SaveOrUpdateRaceStartResponse,
  StartRaces,
  StatusCode,
  Waypoint,
  WaypointType,
  TOAST_STATE
} from "@model";
import {
  CompetitorService,
  EventService,
  ExportService,
  LoaderService,
  MeasurementsService,
  ToastService
} from "@services";
import {ActivatedRoute} from "@angular/router";
import {FormControl, FormGroup} from "@angular/forms";
import {TranslocoService} from "@ngneat/transloco";
import {finalize, forkJoin} from "rxjs";
import { ORGANISER_EVENTS_MANAGEMENT} from 'src/app/app-routing.module';

@Component({
    selector: 'app-event-measurements-management',
    templateUrl: './event-measurements-management.component.html',
    styleUrls: ['./event-measurements-management.component.scss'],
    standalone: false
})
export class EventMeasurementsManagementComponent extends AbstractComponent implements OnInit {
  @Input() waypointList: Waypoint[] = [];
  @Input() eventItem: EventItem | undefined;
  @Input({required: true}) languages: Lookups[] = [];
  @Input({required: true}) countries: Country[] = [];
  @Input() competitors: Competitor[] = [];
  @Input() raceRegistrations: RaceRegistration[] = [];
  @Input() genders: Lookups[] = [];

  filter: PagedFilter<CompetitorFilter> | undefined;
  name: string | undefined;
  organiserId: number | undefined;
  disableRegisterToRace: boolean = false;
  category: number | undefined = undefined;
  gender: number | undefined = undefined;
  page: number = 1;
  selectedStatus: CompetitorStatus | undefined;
  eventHasRoundRace: boolean = false;
  eventHasNormalRace: boolean = false;
  applicationFromSettingsList: ApplicationFormSettings[] = [];
  settingsList: RaceSettings[] = [];
  allWaypointList: Waypoint[] = [];

  searchForm: FormGroup<{ name: FormControl<any>; status: FormControl<any> }> = new FormGroup({
    name: new FormControl(),
    status: new FormControl(),
  });

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private exportService: ExportService,
    private measurementService: MeasurementsService,
    private loaderService: LoaderService,
    private toastService: ToastService,
    private translocoService: TranslocoService,
    private eventService: EventService,
    private competitorService: CompetitorService,
    private cdr: ChangeDetectorRef
  ) {
    super();
  }

  ngOnInit() {
    this.subscribeToFilterChange();
    this.allWaypointList = this.waypointList;
    if ((this.allWaypointList?.length ?? 0) > 0) {
      this.waypointList = this.allWaypointList.filter(item => item.waypointTypeId !== WaypointType.START_WAYPOINT);
    }
    this.applicationFromSettingsList = (this.eventItem?.racesList?.flatMap(item => item.applicationFormSettings) ?? []).filter((item): item is ApplicationFormSettings => !!item);
    this.settingsList = (this.eventItem?.racesList?.flatMap(item => item.raceSettings) ?? []).filter((item): item is RaceSettings => !!item);

    this.disableRegisterToRace = this.isDisableRegisterToRace();

    this.eventHasNormalRace = this.eventItem?.racesList?.find((item: Race | undefined) => !(RaceSettings.isRoundRace(item?.raceSettings?.resultProcessingType) || Race.isBackyardUltra(item?.sport))) ? true : false;
    this.eventHasRoundRace = this.eventItem?.racesList?.find((item: Race | undefined) => (RaceSettings.isRoundRace(item?.raceSettings?.resultProcessingType) || Race.isBackyardUltra(item?.sport))) ? true : false;
  }

  private subscribeToFilterChange(): void {
    this.subscriptions.push(this.searchForm.valueChanges.subscribe(_ => {
        this.name = this.searchForm.controls.name.value;
        this.selectedStatus = this.searchForm.controls.status.value;
      }
    ));
  }

  downloadStartList() {
    this.exportService.downloadStartList(this.eventItem!.id!);
  }

  downloadListOfRaces(type: string) {
    this.exportService.downloadListOfRaces(type, +this.route.snapshot.queryParams['eventId']);
  }

  isDisableRegisterToRace() {
    let disableRegisterToRace: boolean = true;
    if ((this.eventItem?.racesList?.length ?? 0) > 0) {
      for (var race of this.eventItem?.racesList!) {
        if (race.organiserOpen) {
          disableRegisterToRace = false;
          break;
        }
      }
    }
    return disableRegisterToRace;
  }

  downloadCompetitorsListForEvent(type: string, filter: PagedFilter<CompetitorFilter> | undefined) {
    if (filter === undefined || filter === null) {
      let filterNull: CompetitorFilter = new CompetitorFilter();
      filterNull.eventId = +this.route.snapshot.queryParams['eventId'];
      this.filter = new PagedFilter(new Pages(undefined, 1), filterNull);
      filter = this.filter;
    }
    this.exportService.downloadCompetitorsListForEvent(type, filter);
  }

  onSaveOrUpdateRaceStart(startRaces: StartRaces) {
    const { raceStartDateType, races } = startRaces;
    this.loaderService.startLoading();
    this.measurementService.saveOrUpdateRaceStart(this.eventItem!.id!, startRaces, (results: SaveOrUpdateRaceStartResponse[]) => {
      return forkJoin({
        eventItem: this.eventService.findEventByIdProtected(new FetchEventByIdRequest(this.eventItem!.id!), false, true),
        competitors: this.competitorService.findCompetitors(new PagedFilter(new Pages(), {eventId: this.eventItem!.id!}), true, true)
      }).pipe(finalize(() => this.loaderService.dismissLoading())).subscribe(({eventItem, competitors}) => {
        const errorIDs = Array.from(
            new Set(
                results
                    .filter(r => r.statusCode !== StatusCode.OK && r.race?.id != null)
                    .map(r => r.race!.id)
            )
        );

        const allOK = errorIDs.length === 0;
        const anyOK = results.some(r => r.statusCode === StatusCode.OK);

        let resultMessage = (anyOK || allOK)
            ? this.translocoService.translate(`measurements.races.${raceStartDateType}.success`)
            : "";

        if (!allOK && raceStartDateType === RaceStartDateType.STOP) {
          const raceList = errorIDs.join(", ");
          resultMessage += anyOK ? '<br><br>' : '';
          resultMessage += this.translocoService.translate(`measurements.races.${raceStartDateType}.error.checkpoint.data`, {raceList: raceList} )
        }

        this.loaderService.dismissLoading();
        this.toastService.showToast((anyOK || allOK || raceStartDateType !== RaceStartDateType.STOP) ? TOAST_STATE.success : TOAST_STATE.danger, resultMessage);
        this.eventItem = eventItem ?? this.eventItem;
        this.competitors = competitors ?? [];
      });
    }, () => {
      this.loaderService.dismissLoading();
      this.toastService.showToast(TOAST_STATE.danger, this.translocoService.translate(`measurements.races.${raceStartDateType}.error`));
    });
  }

  onChronometarRaceCompetitorStartStop(race:Race) {
    this.measurementService.findRaceStartItems(race.id!).subscribe(raceStartItems => {
      let raceItem:Race|undefined = this.eventItem?.racesList?.find(item => item.id === race!.id!);
      if (raceItem) {
        raceItem!.raceStartItems = raceStartItems;
      }
    });
  }

  onRefreshCounters() {
    this.eventService.findEventByIdProtected(new FetchEventByIdRequest(this.eventItem!.id!), false, true).subscribe(eventItem => {
      this.eventItem = eventItem ?? this.eventItem;
    })
  }

  onChangeTab(id: number | undefined) {
    if (id === 1) {
     this.refreshCompetitors();
    }
    this.cdr.detectChanges();
  }

  refreshCompetitors() {
    window.scrollTo({ top: 0, behavior: 'auto' });
    this.competitorService.findCompetitors(new PagedFilter(new Pages(), {eventId: this.eventItem!.id!}), true, true).subscribe(val => {
      this.competitors = val;
    })
  }



  onRefreshResults() {
    this.loaderService.startLoading()
    this.measurementService.refreshResults(this.eventItem!.id!, () => {
      this.loaderService.dismissLoading();
      this.toastService.showToast(TOAST_STATE.success,
          this.translocoService.translate("race.management.results.refresh.success"));
    }, ()=>{
      this.loaderService.dismissLoading();
      this.toastService.showToast(TOAST_STATE.danger,
          this.translocoService.translate("race.management.results.refresh.error"));
    })
  }

  onGoToEventManagement(): void {
    if (this.eventItem?.id) {
      this.eventService.setCurrentEvent(this.eventItem);
      this.router.navigate([ORGANISER_EVENTS_MANAGEMENT], {queryParams: {eventId: this.eventItem.id}});
    }
  }

  protected readonly RaceRegistration = RaceRegistration;
}
